import { Dispatch, Fragment, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'

import TableRow from '@/modules/shared/components/table/TableRow'
import { ColumnDefinition, MultiRowSelection } from '@/modules/shared/components/table/types'

interface TableTreeRowsProps<T> {
  records: T[]
  columns: ColumnDefinition<T>[]
  /** Depth of these records in the tree (0 = top level). */
  level: number
  keyExtractor: (record: T, index: number) => string
  /** Composed key of the parent row, used to build globally unique path keys. */
  parentPath?: string
  expandedKeys: Set<string>
  onToggleExpand: (pathKey: string, record: T) => void
  getChildren: (record: T) => T[] | undefined
  hasChildren?: (record: T) => boolean
  childrenLoading?: (record: T) => boolean
  className?: string
  onRowClick?: (record: T, index: number) => void
  selectedRows: T[]
  setSelectedRows: Dispatch<SetStateAction<T[]>>
  multiRowSelection?: MultiRowSelection<T>
  selectedRowsFromProps?: T[]
}

export default function TableTreeRows<T>({
  records,
  columns,
  level,
  keyExtractor,
  parentPath,
  expandedKeys,
  onToggleExpand,
  getChildren,
  hasChildren,
  childrenLoading,
  className,
  onRowClick,
  selectedRows,
  setSelectedRows,
  multiRowSelection,
  selectedRowsFromProps,
}: TableTreeRowsProps<T>) {
  const { t } = useTranslation()
  const visibleColumnCount = columns.filter((column) => !column?.hidden || column?.alwaysVisible).length
  const loadingColSpan = visibleColumnCount + (multiRowSelection ? 1 : 0)

  return (
    <>
      {records.map((record, index) => {
        const rowKey = keyExtractor(record, index)
        const pathKey = parentPath ? `${parentPath}/${rowKey}` : rowKey

        const isExpandable = hasChildren ? hasChildren(record) : (getChildren(record)?.length ?? 0) > 0
        const isExpanded = isExpandable && expandedKeys.has(pathKey)
        const isLoadingChildren = childrenLoading?.(record) ?? false
        const children = isExpanded ? getChildren(record) : undefined

        return (
          <Fragment key={pathKey}>
            <TableRow
              record={record}
              index={index}
              columns={columns}
              className={className}
              onRowClick={onRowClick}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              multiRowSelection={multiRowSelection}
              keyExtractor={keyExtractor}
              selectedRowsFromProps={selectedRowsFromProps}
              treeLevel={level}
              treeExpandable={isExpandable}
              treeExpanded={isExpanded}
              onToggleExpand={() => onToggleExpand(pathKey, record)}
              treeChildrenLoading={isExpanded && isLoadingChildren}
            />
            {isExpanded && children && children.length > 0 && (
              <TableTreeRows
                records={children}
                columns={columns}
                level={level + 1}
                keyExtractor={keyExtractor}
                parentPath={pathKey}
                expandedKeys={expandedKeys}
                onToggleExpand={onToggleExpand}
                getChildren={getChildren}
                hasChildren={hasChildren}
                childrenLoading={childrenLoading}
                className={className}
                onRowClick={onRowClick}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                multiRowSelection={multiRowSelection}
                selectedRowsFromProps={selectedRowsFromProps}
              />
            )}
            {isExpanded && !children && isLoadingChildren && (
              <tr className="border-b">
                <td colSpan={loadingColSpan} className="px-4 py-3 text-center text-sm text-gray-500">
                  <span className="inline-flex items-center gap-2" role="status">
                    <span className="size-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                    <span className="sr-only">{t('common.loading', 'Loading')}...</span>
                  </span>
                </td>
              </tr>
            )}
          </Fragment>
        )
      })}
    </>
  )
}
